import type { ReactNode } from "react";
import { useAtom } from "jotai";
import { Link, useOutletContext } from "react-router-dom";

import { configuration } from "../Configuration";
import { useRoleAccess } from "../hooks/useRoleAccess";
import { authenticationAtom } from "../State";
import type { AppShellContext } from "./AppShell";

/** The product page: the product management panel opened on one product (it reads `?product=` on mount). */
export const productPageHref = (productId: string): string =>
  `/settings?panel=productManagement&product=${encodeURIComponent(productId)}`;

/** The read-only product view every signed-in user can open (EntityPage, shown from the schema). */
export const productEntityHref = (productId: string): string => `/entities/Product/${encodeURIComponent(productId)}`;

/** The id behind an asset's `product` — a string, an ObjectId-like, or a loaded product. */
export const productIdOf = (product: unknown): string | null => {
  if (!product) return null;
  if (typeof product === "string") return product.trim() || null;
  if (typeof product === "object") {
    const id = (product as any)._id ?? (product as any).$oid;
    if (id) return productIdOf(id);
    const text = String(product);
    return text && text !== "[object Object]" ? text : null;
  }
  return null;
};

/**
 * Whether the viewer may open the product page — the same gate SettingsPage applies to the panel, so the link never
 * leads to a redirect to the profile. Outside the app shell (no outlet context) only the configured tenants qualify.
 */
export const useMayOpenProductPage = (): boolean => {
  const roleAccess = useRoleAccess();
  const [authentication] = useAtom(authenticationAtom) as any;
  // Plain context read: undefined outside an <Outlet>, never a throw.
  const shell = useOutletContext<AppShellContext | undefined>();
  return (
    !!roleAccess.canSeeProductManagement &&
    (configuration.shouldSeeProductManagement(authentication?.customer) || shell?.hasProductManagementAccess === true)
  );
};

/**
 * An asset's product name, linked to its product: the product management panel for a viewer who may open it, the
 * read-only product view for everyone else — only without a product is it plain text. The click does not bubble, so
 * a table row that opens the asset stays where it is.
 */
export function ProductPageLink({
  product,
  children,
  className,
}: {
  product: unknown;
  children: ReactNode;
  className?: string;
}) {
  const mayOpen = useMayOpenProductPage();
  const productId = productIdOf(product);
  if (!productId || children === null || children === undefined || children === "") {
    return <span className={className}>{children}</span>;
  }
  return (
    <Link
      to={mayOpen ? productPageHref(productId) : productEntityHref(productId)}
      className={`${className ?? ""} text-primary underline-offset-2 hover:underline`.trim()}
      onClick={(event) => event.stopPropagation()}
      data-testid="product-page-link"
    >
      {children}
    </Link>
  );
}
